import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  shareTextChatChunkApplied,
  shareTextDialpadCleared,
} from './shareTextEvents';
import {
  selectShareTextChatText,
  selectShareTextDialpadText,
  selectShareTextValues,
  selectShareTextValuesText,
} from './shareTextSlice';

import type { RootState } from 'features/app/store/store';
import type { ShareTextValueEntry } from 'features/share-text/interfaces';
import { VrsChatEventBus } from 'features/vrs-chat/services';

/**
 * Builds the outbound payload needed to delete one chat character while preserving
 * trailing merged text (for example, dialpad digits appended after chat text).
 *
 * Example:
 * - Ledger text before backspace: "chat12"
 * - Backspace target: remove "t"
 * - Reconstruction payload: "\b\b\b12"
 *
 * The payload removes the trailing segment plus the chat character, then replays
 * the trailing segment so non-chat text remains visible on the endpoint.
 */
const getBackspaceReconstructionChunk = (entries: ShareTextValueEntry[]) => {
  let lastChatEntryIndex = -1;

  for (let i = entries.length - 1; i >= 0; i -= 1) {
    if (entries[i].source === 'chat') {
      lastChatEntryIndex = i;
      break;
    }
  }

  if (lastChatEntryIndex < 0) {
    return '';
  }

  const trailingText = entries
    .slice(lastChatEntryIndex + 1)
    .map((entry) => entry.value)
    .join('');

  return `${'\b'.repeat(trailingText.length + 1)}${trailingText}`;
};

type SyncChatChunkThunkResult = ThunkAction<
  string,
  RootState,
  unknown,
  UnknownAction
>;

/**
 * Normalizes chat input into the exact outbound chunk to send over the data channel.
 *
 * For normal characters, the outbound chunk is unchanged.
 * For backspace, the thunk may emit a reconstruction sequence so chat deletion does
 * not accidentally remove trailing dialpad text on the endpoint.
 *
 * Returns the final chunk that should be queued for outbound transmission.
 */
export const syncChatChunkToShareTextThunk = (
  chatChunk: string
): SyncChatChunkThunkResult => {
  return (dispatch, getState) => {
    let outboundChunkToSend = '';

    for (const char of chatChunk) {
      if (char === '\b') {
        const stateBeforeBackspace = getState();
        const entriesBeforeBackspace =
          selectShareTextValues(stateBeforeBackspace);
        const reconstructionChunk = getBackspaceReconstructionChunk(
          entriesBeforeBackspace
        );

        if (!reconstructionChunk) {
          continue;
        }

        outboundChunkToSend += reconstructionChunk;
        dispatch(shareTextChatChunkApplied(char));
        continue;
      }

      outboundChunkToSend += char;
      dispatch(shareTextChatChunkApplied(char));
    }

    return outboundChunkToSend;
  };
};

export const clearDialpadShareTextThunk = createAsyncThunk(
  'shareText/clearDialpadShareText',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const shareTextDialpadText = selectShareTextDialpadText(state);

    if (!shareTextDialpadText) {
      return;
    }

    const shareTextValuesText = selectShareTextValuesText(state);
    const shareTextChatText = selectShareTextChatText(state);
    const encoder = new TextEncoder();
    const backspaceSequence = '\b'.repeat(shareTextValuesText.length);

    if (backspaceSequence) {
      VrsChatEventBus.$sendMessage.next(encoder.encode(backspaceSequence));
    }

    if (shareTextChatText) {
      VrsChatEventBus.$sendMessage.next(encoder.encode(shareTextChatText));
    }

    dispatch(shareTextDialpadCleared());
  }
);
